import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Box, Chip, FormGroup, TextField, Typography } from '@mui/material';
import { reactiveVars, UserDetailsProps } from '@brainpool/apollo-connect';
import { ModalProps } from '../../../types/ModalTypes';

import { ListGroupsProps, useListGroups } from '../../../hooks/useListGroups';
import {
  UpdateGroupMembersInput,
  useUpdateGroupMembers,
} from '../../../hooks/useUpdateGroupMembers';

import { UserWithRoles } from '../../../types/User';

import Button from '@brainpool/components/atoms/Button/Button';
import { pluralise } from '@brainpool/utils';
import { useReactiveVar } from '@apollo/client/react';
import Modal from '@brainpool/components/molecules/Modal/Modal';
import { Case } from '../../../types/Case';
import { filterProjectsByOwnership } from '../../../utils/commonFunctions';

interface FormData {
  users: UserWithRoles[];
}

export function ShareModal({ data, handleClose, open = false, type }: ModalProps) {
  const currentUser = useReactiveVar<UserDetailsProps>(reactiveVars.userDetails);
  const [ownedProjects, setOwnedProjects] = useState<Partial<Case>[]>([]);
  const [nonOwnedProjects, setNonOwnedProjects] = useState<Partial<Case>[]>([]);
  const [ownedProjectIDs, setOwnedProjectIDs] = useState<string[]>([]);
  const [shouldAutoClose, setShouldAutoClose] = useState(false);
  const caseIds: ListGroupsProps[] = ownedProjectIDs?.map(id => ({ case_id: id })) || [];

  useEffect(() => {
    if (data && data.length > 0) {
      const { ownedProjects, nonOwnedProjects, ownedProjectIDs } = filterProjectsByOwnership(data);
      setOwnedProjects(ownedProjects);
      setNonOwnedProjects(nonOwnedProjects);
      setOwnedProjectIDs(ownedProjectIDs);
    }
  }, [data]);

  const {
    listGroups: listAllUsers,
    data: allUsersData,
    loading: allUsersLoading,
  } = useListGroups({ useCache: true });

  const allUsers = allUsersData?.listGroups[0]?.items || [];

  const {
    listGroups: listCaseGroups,
    data: caseGroupsData,
    loading: caseGroupsLoading,
  } = useListGroups({ useCache: true });

  const {
    updateGroupMembers,
    data: updateGroupMembersData,
    loading: updateGroupLoading,
  } = useUpdateGroupMembers();

  useEffect(() => {
    listAllUsers([{ name: 'All Users' }], 100);
  }, [data]);

  useEffect(() => {
    if (allUsersData?.listGroups[0]?.items?.length) {
      listCaseGroups(caseIds, 100);
    }
  }, [allUsersData]);

  useEffect(() => {
    reactiveVars.isLoading(updateGroupLoading);
  }, [updateGroupLoading]);

  useEffect(() => {
    if (shouldAutoClose && updateGroupMembersData && !updateGroupLoading && handleClose) {
      handleClose();
      setShouldAutoClose(false);
    }
  }, [shouldAutoClose, updateGroupMembersData, updateGroupLoading, handleClose]);

  const userOptions = [
    { identifier: 'All Users', id: 'allUsers' },
    ...(allUsers[0]?.members || []).filter(
      ({ id, identifier }) => id !== currentUser.userId && identifier !== currentUser.email
    ),
  ];

  const handleShare = async (formData: FormData) => {
    const isAllUsersSelected = formData.users.some(user => user.id === 'allUsers');
    const groupMembers = isAllUsersSelected
      ? allUsers?.[0]?.members.map(user => user.id) || []
      : formData.users.map(user => user.id);

    //get project from ownedProjects and find correct group id from caseGroupsData
    const updateGroups: UpdateGroupMembersInput[] = ownedProjects
      .map(({ id }) => {
        const groupID = caseGroupsData?.listGroups.find(({ caseId }) => caseId === id)?.items?.[0]
          ?.id;

        if (!groupID) return null;
        return {
          id: groupID,
          members: groupMembers,
          action: 'ADD',
        };
      })
      .filter((group): group is UpdateGroupMembersInput => group !== null);

    updateGroupMembers({
      groups: updateGroups,
    });
    setShouldAutoClose(true);
  };

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    defaultValues: {
      users: [],
    },
    mode: 'onChange',
  });
  return (
    <Modal
      titleType="h3"
      title={`Share ${pluralise('Project', ownedProjects.length)}`}
      open={open}
      onClose={
        handleClose ??
        (() => {
          // do nothing
        })
      }
    >
      <Box
        gap={2}
        component="form"
        onSubmit={handleSubmit(handleShare)}
        data-testid="share-project-form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {allUsers.length < 1 && (
          <Typography sx={{ margin: '1rem 0' }}>No users available to share with.</Typography>
        )}
        {allUsers.length > 0 && ownedProjects.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
            }}
          >
            <FormGroup sx={{ width: '100%' }}>
              <Controller
                name="users"
                control={control}
                rules={{
                  required: 'Email is required',
                }}
                render={({ field: { onChange, value, ref, ...field }, fieldState: { error } }) => (
                  <Autocomplete
                    sx={{ width: '100%' }}
                    multiple
                    disabled={!allUsers}
                    options={userOptions || []}
                    getOptionLabel={option => option.identifier || ''}
                    onChange={(event, newValue) => {
                      onChange(newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      onChange(newInputValue);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        id="email"
                        type="email"
                        placeholder={value?.length ? '' : 'Enter email address'}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </FormGroup>
            <Button
              label="Invite"
              variant="contained"
              disabled={!isValid}
              type="submit"
              sx={{ ml: 1 }}
            />
          </Box>
        )}
        {allUsers.length > 0 && ownedProjects.length > 0 && (
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography variant="body1">
              Are you sure you want to share {pluralise('this', ownedProjects.length)}{' '}
              {pluralise('project', ownedProjects.length)}?
            </Typography>
            {ownedProjects.map(
              ({ role, id, details }) =>
                role === 'Owner' && (
                  <Chip
                    key={id}
                    label={details?.name}
                    sx={{ margin: '0.5rem 0.25rem' }}
                    color="success"
                    variant="outlined"
                  />
                )
            )}
          </Box>
        )}
        {allUsers.length > 0 && nonOwnedProjects.length > 0 && (
          <Box sx={{ marginBottom: '1rem' }}>
            <Typography variant="body1">
              You do not have permission to share {pluralise('this', nonOwnedProjects.length)}{' '}
              {pluralise('project', nonOwnedProjects.length)}.
            </Typography>

            {nonOwnedProjects.map(
              ({ role, id, details }) =>
                role !== 'Owner' && (
                  <Chip
                    key={id}
                    label={details?.name}
                    sx={{ margin: '0.5rem 0.25rem' }}
                    color="error"
                    variant="outlined"
                  />
                )
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
export default ShareModal;
